import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  useTheme,
  Button,
  Typography,
  Card,
  CardActions,
  CardContent,
  darken,
  IconButton,
  useMediaQuery,
  Tabs,
  Tab, TextField, InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

import { Close, ArrowBack } from "@mui/icons-material";

import { setAlert } from "../features/slices/alertSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { openModal, closeModal } from "../features/slices/modalSlice";

import Image from "../components/common/Image";
import Backdrop from "../components/common/Backdrop";
import DOMPurify from "dompurify";

import {
  useLazyGetBonusListQuery,
  useLazyGetBonusDetailsQuery,
  useClaimBonusMutation,
} from "../features/api/bonusApiSlice";
import {
  useLazyGetPublicBonusListQuery,
  useLazyGetPublicBonusDetailsQuery,
} from "../features/api/publicApiSlice";
import { lighten } from "@material-ui/core";
import * as Constant from "../features/constant";
import CopiedTooltip from "../components/common/CopiedTooltip";
import { useGetShareLinkQuery } from "../features/api/userApiSlice";
import TextCarousel from "../components/common/TextCarousel";
import { useGetGameArticlesQuery } from "../features/api/homeApiSlice";

const BonusV1_1 = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const [bonusCategory, setBonusCategory] = useState("ALL");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isClaimTrigger, setIsClaimTrigger] = useState(false);
  const [bonusType, setBonusType] = useState(null);
  const [isBonusSelected, setIsBonusSelected] = useState(false);

  // New state for tab switching
  const [activeTab, setActiveTab] = useState(0); // 0 = Promotions, 1 = Mini Games

  const lighter = lighten(theme.palette.primary.main, 0.1);
  const darker = darken(theme.palette.primary.main, 0.05);

  // Color constants for the pill tab bar and mini game section
  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";
  const PRIMARY = theme.palette.primary.main;
  const PAPER = theme.palette.background.paper;
  const TEXT = process.env.REACT_APP_COLOR_TEXT || "#333";
  const MUTED = "#888";
  const DIVIDER = process.env.REACT_APP_COLOR_DIVIDER1 || "#e0e0e0";
  const PILL_BG = isLightTheme ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)";

  const [
    getBonusList,
    {
      data: bonusList,
      isFetching: isBonusListFetching,
      isLoading: isBonusListLoading,
      isError: isBonusListError,
      isSuccess: isBonusListSuccess,
      error: bonusListError,
    },
  ] = useLazyGetBonusListQuery({ prefetch: true });

  const [
    getPublicBonusList,
    {
      data: publicBonusList,
      isFetching: isPublicBonusListFetching,
      isLoading: isPublicBonusListLoading,
      isError: isPublicBonusListsError,
      isSuccess: isPublicBonusListSuccess,
      error: publicBonusListError,
    },
  ] = useLazyGetPublicBonusListQuery({ prefetch: true });

  const [
    getPublicBonusDetails,
    {
      data: publicBonusDetails,
      isFetching: isPublicBonusDetailsFetching,
      isLoading: isPublicBonusDetailsLoading,
      isError: isPublicBonusDetailsError,
      isSuccess: isPublicBonusDetailsSuccess,
      error: publicBonusDetailsError,
    },
  ] = useLazyGetPublicBonusDetailsQuery({ prefetch: true });

  const [
    getBonusDetails,
    {
      data: bonusDetails,
      isFetching: isBonusDetailsFetching,
      isLoading: isBonusDetailsLoading,
      isError: isBonusDetailsError,
      isSuccess: isBonusDetailsSuccess,
      error: bonusDetailsError,
    },
  ] = useLazyGetBonusDetailsQuery({ prefetch: true });

  const [
    claim,
    {
      data: claimBonus,
      isFetching: isClaimBonusFetching,
      isLoading: isClaimBonusLoading,
      isError: isClaimBonusError,
      isSuccess: isClaimBonusSuccess,
      error: claimBonusError,
    },
  ] = useClaimBonusMutation({ prefetch: true });

  const {
    data: shareLink,
    isLoading: isShareLinkLoading,
    isError: isShareLinkError,
    isSuccess: isShareLinkSuccess,
    error: shareLinkError,
  } = useGetShareLinkQuery();

  const { data: gameArticles } = useGetGameArticlesQuery();

  const handleClaimBonus = async (bonusCode) => {
    let data = {
      bonusCode: bonusCode,
    };
    await claim(data).unwrap();
  };

  const bonusSeo = t("promotion.seo") !== "promotion.seo" ? (
    <>
      <Typography mt={2} color={process.env.REACT_APP_COLOR_TEXT || "white"} dangerouslySetInnerHTML={{ __html: t("promotion.seo") }}></Typography>
    </>
  ) : null;

  const handleArticleReadMore = (article) => {
    let imgs = [];
    try { imgs = JSON.parse(article.imageUrls || "[]"); } catch { /* */ }

    dispatch(openModal({
      title: article.name,
      content: (
        <>
          {(article.bannerUrl || article.thumbnailUrl) && (
            <Box sx={{ position: "relative", width: "100%", paddingTop: "31.25%", borderRadius: "8px", overflow: "hidden", backgroundColor: "#eee", mb: 1.5 }}>
              <Box
                component="img"
                src={article.bannerUrl || article.thumbnailUrl}
                alt={article.name}
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}
          {article.content && (
            <Box
              sx={{ fontSize: 13, color: TEXT, lineHeight: 1.6, mb: 1.5, "& p": { mt: 0, mb: 0.75 } }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />
          )}
          {imgs.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", mb: 1.5 }}>
              {imgs.map((url, i) => (
                <Box
                  key={i}
                  component="img"
                  src={url}
                  sx={{ width: "calc(50% - 4px)", flexShrink: 0, borderRadius: "6px" }}
                />
              ))}
            </Box>
          )}
          <Box sx={{ mb: 1.5 }}>
            {!token ? (
              <Button
                variant="contained"
                fullWidth
                sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, backgroundColor: PRIMARY, "&:hover": { backgroundColor: darken(PRIMARY, 0.1) } }}
                onClick={() => { dispatch(closeModal()); navigate(Constant.getPath("/register")); }}
              >
                {t("promotion.register")}
              </Button>
            ) : article.link ? (
              <Button
                variant="contained"
                fullWidth
                sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, backgroundColor: PRIMARY, "&:hover": { backgroundColor: darken(PRIMARY, 0.1) } }}
                onClick={() => window.open(article.link, "_blank")}
              >
                {t("minigame.playnow") !== "minigame.playnow" ? t("minigame.playnow") : "Play Now"}
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                disabled
                sx={{ borderRadius: "8px", fontWeight: 700, py: 1.25, "&.Mui-disabled": { backgroundColor: PRIMARY, color: "#fff", opacity: 0.6 } }}
              >
                {t("minigame.comingsoon") !== "minigame.comingsoon" ? t("minigame.comingsoon") : "Coming Soon"}
              </Button>
            )}
          </Box>
          {article.termsAndConditions && (
            <Accordion sx={{ backgroundColor: PAPER, boxShadow: "none", border: `1px solid ${DIVIDER}`, borderRadius: "8px !important", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TEXT }} />}>
                <Typography fontWeight={600} fontSize={13} color={TEXT}>
                  {t("minigame.termsandconditions") !== "minigame.termsandconditions" ? t("minigame.termsandconditions") : "Terms And Conditions"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ fontSize: 12, color: TEXT, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.termsAndConditions) }} />
              </AccordionDetails>
            </Accordion>
          )}
        </>
      ),
      button: [],
    }));
  };

  useEffect(() => {
    if (isClaimBonusError) {
      dispatch(
        openModal({
          title: isClaimBonusError?.data?.message,
        })
      );
    }
  }, [isClaimBonusError]);


  useEffect(() => {
    if (!isClaimBonusLoading && isClaimBonusSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("promotion.successmessage"),
          variant: "success",
        })
      );
      // window.fbq('trackCustom', 'CompletePromotion');
      dispatch(closeModal());
    }
  }, [isClaimBonusLoading, isClaimBonusSuccess, isClaimBonusError]);

  useEffect(() => {
    getPublicBonusList(bonusCategory);
    getBonusList(bonusCategory);
  }, [bonusCategory]);

  useEffect(() => {
    // Reset the border for all buttons to transparent
    const allButtons = document.querySelectorAll('[id^="button-"]');
    allButtons.forEach((button) => {
      button.style.border = '6px solid transparent';
    });

    // Apply the red border to the selected button
    const selectedButton = document.getElementById(`button-${selectedItem?.bonusCode}`);
    if (selectedButton) {
      selectedButton.style.border = `6px solid ${theme.palette.primary.main}`;
    }
  }, [selectedItem]);

  useEffect(() => {
    if (
      (!isBonusDetailsLoading && isBonusDetailsSuccess) ||
      (!isPublicBonusDetailsLoading && isPublicBonusDetailsSuccess)
    ) {

      if (isClaimTrigger) {
        dispatch(
          openModal({
            title:
              publicBonusDetails?.result?.displayTitle ||
              bonusDetails?.result?.displayTitle,
            content: (
              <>
                {(bonusList || publicBonusList)?.result?.bonus?.map((item, index) => {
                  const isSelected = selectedItem === item;

                  if (item.bonusType === bonusType) {
                    return (
                      <Grid item xs={1} sm={1} md={1} key={index}>
                        <Button
                          id={`button-${item?.bonusCode}`}
                          variant="text"
                          onClick={() => {
                            const newSelectedItem = isSelected ? null : item;

                            setSelectedItem(newSelectedItem);
                            setIsBonusSelected(!!newSelectedItem); // Set to true if a bonus is selected
                          }}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            border: isSelected ? theme.palette.primary.main : '6px solid transparent',
                            "&:hover": {
                              border: isSelected ? theme.palette.primary.main : '6px solid transparent',
                            },
                            padding: '0'
                          }}
                        >
                          <Card
                            variant="outlined"
                            display="flex"
                            flexDirection="column"
                            sx={{
                              p: 1,
                              bgcolor: theme.palette?.background?.paper,
                              borderWidth: 0,
                              padding: '0'
                            }}
                          >
                            <Image src={item.thumbnailUrl} alt={Constant.ALT_TAG_BONUS_IMG} />
                          </Card>
                        </Button>
                      </Grid>
                    );
                  } else {
                    return null; // Skip rendering if bonusCode doesn't match
                  }
                })}
              </>
            ),
            button: !token
              ? [
                {
                  label: t("promotion.login"),
                  onSubmit: () => {
                    dispatch(closeModal());
                    navigate(Constant.getPath("/login"))
                  },
                },
                {
                  label: t("promotion.register"),
                  onSubmit: () => {
                    dispatch(closeModal());
                    navigate(Constant.getPath("/register"))
                  },
                },
              ]
              : token && bonusDetails?.result?.claimable
                ? [
                  {
                    label: t("promotion.claim"),
                    onSubmit: () => {
                      dispatch(closeModal());
                      handleClaimBonus(
                        selectedItem?.bonusCode
                      );
                    },
                    disabled:
                      !selectedItem?.isActivated,
                  },
                ]
                : [],
          })
        );
      } else {
        setIsClaimTrigger(false);
        dispatch(
          openModal({
            title:
              publicBonusDetails?.result?.displayTitle ||
              bonusDetails?.result?.displayTitle,
            content: (
              <>
                <Image
                  src={
                    (publicBonusDetails?.result?.bonusTypeThumbnailUrl || publicBonusDetails?.result?.thumbnailUrl) ||
                    (bonusDetails?.result?.bonusTypeThumbnailUrl || bonusDetails?.result?.thumbnailUrl)
                  }
                  sx={{
                    width: "80%",
                  }}
                  alt={Constant.ALT_TAG_BONUS_IMG}
                />
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      publicBonusDetails?.result?.content ||
                      bonusDetails?.result?.content
                    ),
                  }}
                ></div>
                {token && bonusDetails?.result?.bonusCode === 'B044' && (
                  <div style={{
                    display: 'flex', justifyContent: 'center',
                    position: "absolute", bottom: 0, width: "100%", padding: "20px",
                    background: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper, left: 0
                  }}>
                    <TextField
                      label={t("walletcard.myaccountpage.referrallink")}
                      value={shareLink?.result?.url}
                      InputLabelProps={{ shrink: true }}
                      disabled
                      type="text"
                      size="small"
                      sx={{ width: "400px" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              disableRipple
                              sx={{
                                "&.MuiButtonBase-root:hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              <CopiedTooltip text={shareLink?.result?.url} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                )}

              </>
            ),
            button: !token
              ? [
                {
                  label: t("promotion.login"),
                  onSubmit: () => {
                    dispatch(closeModal());
                    navigate(Constant.getPath("/login"))
                  },
                },
                {
                  label: t("promotion.register"),
                  onSubmit: () => {
                    dispatch(closeModal());
                    navigate(Constant.getPath("/register"))
                  },
                },
              ]
              : [],
          })
        );
      }
    }
  }, [bonusDetails, publicBonusDetails, token, selectedItem]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.secondaryPaper,
      }}
    >
      {mobileView && (
        <Grid container justify="center" py={1}>
          <Grid item xs={4}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs={4} alignSelf="center">
            <Typography
              textAlign="center"
              fontWeight={600}
              color={theme.palette.primary.main}
            >
              {t("promotion.title")}
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Pill-style Promotions / Mini Games toggle — hidden when no game articles */}
      {gameArticles?.result?.length > 0 && <Box
      sx={{ 
        backgroundColor: PAPER,
        // borderBottom: `1px solid "${DIVIDER}"`, 
        mb:-1,
      }}>
        <Box sx={{ maxWidth: { md: 960 }, mx: { md: "auto" }, px: 2, py: 1 }}>
          <Box sx={{ display: "flex", backgroundColor: PILL_BG, borderRadius: "999px", p: "3px" }}>
            {[
              t("bonus.promotions") !== "bonus.promotions" ? t("bonus.promotions") : "Promotions",
              t("bonus.minigames") !== "bonus.minigames" ? t("bonus.minigames") : "Mini Games",
            ].map((label, i) => (
              <Box
                key={i}
                onClick={() => setActiveTab(i)}
                sx={{
                  flex: 1, textAlign: "center", py: 0.9,
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                  borderRadius: "999px",
                  backgroundColor: activeTab === i ? PAPER : "transparent",
                  color: activeTab === i ? PRIMARY : MUTED,
                  boxShadow: activeTab === i ? "0 1px 5px rgba(0,0,0,0.18)" : "none",
                  transition: "background-color 0.2s, color 0.2s, box-shadow 0.2s",
                  userSelect: "none",
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>}

      {/* Promotion category tabs — only shown on Promotions tab */}
      {activeTab === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            backgroundColor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
          }}
        >
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {publicBonusList?.result?.bonusCategory?.map((category, index) => (
              <>
                <Tab
                  key={index}
                  label={category.label}
                  sx={{
                    color: theme.palette.primary.main,
                    borderBottom:
                      bonusCategory === category.code &&
                      `2px solid ${theme.palette.primary.main}`,
                    fontWeight: 600,
                  }}
                  onClick={() => setBonusCategory(category.code)}
                />
              </>
            ))}
          </Tabs>
        </Box>
      )}

      {(isClaimBonusLoading || isClaimBonusFetching) && <Backdrop />}

      <Container
        sx={{
          px: mobileView && 0,
        }}
      >
        <Box
          pt={{ xs: 1, sm: 3, lg: 3, xl: 3 }}
          pb={{ xs: 8, sm: 3, lg: 3, xl: 3 }}
          sx={{
            minHeight: mobileView && "100vh",
          }}
        >
          {/* PROMOTIONS */}
          {activeTab === 0 && (
            <Grid
              container
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              {(bonusList || publicBonusList)?.result?.bonus?.map(
                (list, index) => (
                  (list.bonusTypeDisplay || list.bonusType === 'DEPOSIT' || list.bonusType === 'CUSTOM' || list.bonusType === 'REFERRAL' || list.bonusType === 'RESCUE' || list.bonusType === 'RESCUE_ESPORT') ? (
                    <Grid item xs={12} sm={6} md={6} key={index}>
                      <Card
                        variant="outlined"
                        display="flex"
                        flexDirection="column"
                        sx={{
                          p: 1,
                          bgcolor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                          borderWidth: 0,
                        }}
                      >
                        <Image src={list.bonusTypeDisplay ? list.bonusTypeThumbnailUrl : list.thumbnailUrl} alt={Constant.ALT_TAG_BONUS_IMG} />
                        <CardContent
                          display="flex"
                          flexDirection="column"
                          sx={{ p: 1 }}
                        >
                          <Grid container spacing={2}>
                            {list?.bonusCategory?.map((cat) => (
                              <Grid item>
                                <Box
                                  sx={{
                                    borderRadius: 1,
                                    backgroundColor: darken(
                                      process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                                      0.2
                                    ),
                                    px: 1,
                                  }}
                                >
                                  <Typography color={process.env.REACT_APP_COLOR_TEXT || "white"} fontSize={11}>
                                    {cat}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                          <Typography
                            mt={1}
                            fontWeight={600}
                            color={theme.palette.primary.main}
                          >
                            {list.displayTitle}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color={process.env.REACT_APP_COLOR_TEXT || "white"}
                            fontSize={14}
                          >
                            {list.description}
                          </Typography>
                        </CardContent>
                        <CardActions
                          sx={{
                            display: "flex",
                            justifyContent: list.claimable
                              ? "center"
                              : "space-between",
                          }}
                        >
                          {token && list.claimable && (
                            <Button
                              variant="outlined"
                              sx={{
                                p: 1,
                                fontWeight: "600",
                                color: theme.palette.primary.main,
                                borderRadius: "10px",
                                fontSize: "12px",
                                width: "100%",
                                "&.Mui-disabled": {
                                  backgroundColor: lighten(
                                    process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                                    0.2
                                  ),
                                  color: lighten(
                                    theme.palette.background.paper,
                                    0.02
                                  ),
                                },
                              }}
                              onClick={() => {
                                if (token && bonusList?.result && (list.bonusType === 'CUSTOM' || list.bonusType === 'REFERRAL' || list.bonusType === 'DEPOSIT' || list.bonusType === 'RESCUE' || list.bonusType === 'RESCUE_ESPORT')) {
                                  handleClaimBonus(
                                    list?.bonusCode
                                  );
                                }
                                else if (token && bonusList?.result) {
                                  getBonusDetails(list.bonusCode);
                                  setSelectedItem(null);
                                  dispatch(
                                    openModal({
                                      title:
                                        publicBonusDetails?.result?.displayTitle ||
                                        bonusDetails?.result?.displayTitle,
                                      content: (
                                        <>
                                          {(bonusList || publicBonusList)?.result?.bonus?.map((item, index) => {
                                            const isSelected = selectedItem === item;

                                            if (item.bonusType === list.bonusType) {
                                              return (
                                                <Grid item xs={1} sm={1} md={1} key={index}>
                                                  <Button
                                                    id={`button-${item?.bonusCode}`}
                                                    variant="text"
                                                    onClick={() => setSelectedItem(isSelected ? null : item)}
                                                    sx={{
                                                      textDecoration: 'none',
                                                      color: 'inherit',
                                                      border: isSelected ? theme.palette.primary.main : '6px solid transparent',
                                                      "&:hover": {
                                                        border: isSelected ? theme.palette.primary.main : '6px solid transparent',
                                                      },
                                                      padding: '0'
                                                    }}
                                                  >
                                                    <Card
                                                      variant="outlined"
                                                      display="flex"
                                                      flexDirection="column"
                                                      sx={{
                                                        p: 1,
                                                        bgcolor: theme.palette?.background?.paper,
                                                        borderWidth: 0,
                                                        padding: '0'
                                                      }}
                                                    >
                                                      <Image src={item.thumbnailUrl} alt={Constant.ALT_TAG_BONUS_IMG} />
                                                    </Card>
                                                  </Button>
                                                </Grid>
                                              );
                                            } else {
                                              return null; // Skip rendering if bonusCode doesn't match
                                            }
                                          })}
                                        </>
                                      ),
                                      button: !token
                                        ? [
                                          {
                                            label: t("promotion.login"),
                                            onSubmit: () => {
                                              dispatch(closeModal());
                                              navigate(Constant.getPath("/login"))
                                            },
                                          },
                                          {
                                            label: t("promotion.register"),
                                            onSubmit: () => {
                                              dispatch(closeModal());
                                              navigate(Constant.getPath("/register"))
                                            },
                                          },
                                        ]
                                        : token && bonusDetails?.result?.claimable
                                          ? [
                                            {
                                              label: t("promotion.claim"),
                                              onSubmit: () => {
                                                dispatch(closeModal());
                                                handleClaimBonus(
                                                  selectedItem?.bonusCode
                                                );
                                              },
                                              disabled:
                                                !selectedItem?.isActivated,
                                            },
                                          ]
                                          : [],
                                    })
                                  );
                                  setIsClaimTrigger(true);
                                  setBonusType(list.bonusType);
                                } else {
                                  navigate(Constant.getPath("/register"))
                                }
                              }}
                              disabled={
                                token
                                  ? !list.isActivated || isClaimBonusLoading
                                  : false
                              }
                            >
                              {token
                                ? t("promotion.claim")
                                : t("promotion.register")}
                            </Button>
                          )}
                          {!token && (
                            <Button
                              variant="outlined"
                              sx={{
                                p: 1,
                                fontWeight: "600",
                                color: theme.palette.primary.main,
                                borderRadius: "10px",
                                fontSize: "12px",
                                width: "100%",
                              }}
                              onClick={() => navigate(Constant.getPath("/register"))}
                            >
                              {t("promotion.register")}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            sx={{
                              p: 1,
                              ml: 1,
                              fontWeight: "600",
                              width: "100%",
                              borderRadius: "10px",
                              fontSize: "12px",
                              backgroundImage: `linear-gradient(to bottom, ${lighter} 50%, ${darker} 50%)`,
                              backgroundSize: "100% 100%",
                            }}
                            onClick={() => {
                              getBonusDetails(list.bonusCode);
                              getPublicBonusDetails(list.bonusCode);
                              setIsClaimTrigger(false);
                              if ((isPublicBonusListSuccess && !isPublicBonusDetailsLoading) ||
                                (isBonusDetailsSuccess && !isBonusDetailsLoading)) {
                                dispatch(
                                  openModal({
                                    title:
                                      publicBonusDetails?.result?.displayTitle ||
                                      bonusDetails?.result?.displayTitle,
                                    content: (
                                      <>
                                        <Image
                                          src={
                                            (publicBonusDetails?.result?.bonusTypeThumbnailUrl || publicBonusDetails?.result?.thumbnailUrl) ||
                                            (bonusDetails?.result?.bonusTypeThumbnailUrl || bonusDetails?.result?.thumbnailUrl)
                                          }
                                          sx={{
                                            width: "80%",
                                          }}
                                          alt={Constant.ALT_TAG_BONUS_IMG}
                                        />
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                              publicBonusDetails?.result?.content ||
                                              bonusDetails?.result?.content
                                            ),
                                          }}
                                        ></div>
                                      </>
                                    ),
                                    button: !token
                                      ? [
                                        {
                                          label: t("promotion.login"),
                                          onSubmit: () => {
                                            dispatch(closeModal());
                                            navigate(Constant.getPath("/login"))
                                          },
                                        },
                                        {
                                          label: t("promotion.register"),
                                          onSubmit: () => {
                                            dispatch(closeModal());
                                            navigate(Constant.getPath("/register"))
                                          },
                                        },
                                      ]
                                      : [],
                                  })
                                );
                              }
                            }}
                          >
                            {t("promotion.readmore")}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ) : null
                )
              )}
            </Grid>
          )}

          {/* MINI GAMES */}
          {activeTab === 1 && (
            <Grid
              container
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              {(gameArticles?.result || []).map((article, index) => (
                <Grid item xs={12} sm={6} md={6} key={index}>
                  <Card
                    variant="outlined"
                    display="flex"
                    flexDirection="column"
                    sx={{
                      p: 1,
                      bgcolor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                      borderWidth: 0,
                    }}
                  >
                    <Image src={article.bannerUrl || article.thumbnailUrl} alt={article.name} />
                    <CardContent
                      display="flex"
                      flexDirection="column"
                      sx={{ p: 1 }}
                    >
                      <Typography
                        mt={1}
                        fontWeight={600}
                        color={theme.palette.primary.main}
                      >
                        {article.name}
                      </Typography>
                    </CardContent>
                    <CardActions
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {!token ? (
                        <Button
                          variant="outlined"
                          sx={{
                            p: 1,
                            fontWeight: "600",
                            color: theme.palette.primary.main,
                            borderRadius: "10px",
                            fontSize: "12px",
                            width: "100%",
                          }}
                          onClick={() => navigate(Constant.getPath("/register"))}
                        >
                          {t("promotion.register")}
                        </Button>
                      ) : article.link ? (
                        <Button
                          variant="outlined"
                          sx={{
                            p: 1,
                            fontWeight: "600",
                            color: theme.palette.primary.main,
                            borderRadius: "10px",
                            fontSize: "12px",
                            width: "100%",
                          }}
                          onClick={() => window.open(article.link, "_blank")}
                        >
                          {t("minigame.playnow") !== "minigame.playnow" ? t("minigame.playnow") : "Play Now"}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          disabled
                          sx={{
                            p: 1,
                            fontWeight: "600",
                            borderRadius: "10px",
                            fontSize: "12px",
                            width: "100%",
                            "&.Mui-disabled": {
                              backgroundColor: lighten(
                                process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                                0.2
                              ),
                              color: lighten(theme.palette.background.paper, 0.02),
                            },
                          }}
                        >
                          {t("minigame.comingsoon") !== "minigame.comingsoon" ? t("minigame.comingsoon") : "Coming Soon"}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        sx={{
                          p: 1,
                          ml: 1,
                          fontWeight: "600",
                          width: "100%",
                          borderRadius: "10px",
                          fontSize: "12px",
                          backgroundImage: `linear-gradient(to bottom, ${lighter} 50%, ${darker} 50%)`,
                          backgroundSize: "100% 100%",
                        }}
                        onClick={() => handleArticleReadMore(article)}
                      >
                        {t("promotion.readmore")}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        {/* SEO text */}
        {!mobileView && bonusSeo && (
              <Box pt={{ xs: 4, sm: 4, md: 4 }} pb={{ xs: 11, sm: 5, md: 5 }}>
                <TextCarousel
                  html={[bonusSeo]}
                  slidesPerView={1}
                  pagination={true}
                  spaceBetween={0}
                />
              </Box>
        )}
      </Container>
    </Box>
  );
};

export default BonusV1_1;
